/**
 * Container Frame helpers — DL-2.5.
 *
 * Two things live here:
 *
 *   1. `NETWORK_CONTAINER_MANIFEST_IDS` — the canonical set of AWS
 *      manifest ids that should be rendered as visual *Frames* on the
 *      canvas (wrapping their children) rather than as standalone
 *      shapes. The hydrate path uses this; the DL-2 pattern + compose
 *      tools now use it too so chat-created compositions get the
 *      same nested-box visualization as imported scans.
 *
 *   2. `planContainerFrames(...)` — pure layout function. Given a
 *      containment graph + child positions/dims, returns ordered
 *      Frame descriptors (bottom-up so each frame's bounds can
 *      encompass either direct leaves or already-built child frames).
 *
 * Why pure + shared, not ad-hoc per tool:
 *   - `applyPatternTool` + `composeServiceTool` produce semantically
 *     identical scenes — the Frame layout MUST agree.
 *   - The hydrate path in `hydrateImportedResources.buildContainerFrames`
 *     can eventually delegate here, eliminating the duplicate
 *     bottom-up logic. (For this cut we leave hydrate alone — it's
 *     working — and only consume the helper from the new tools.)
 */

import {
  ALL_PROVIDER_SHAPE_SPECS,
  CONTAINER_PROVIDER_SHAPE_IDS,
  getProviderShapeSpec,
} from '../providers/providerDialectRegistry';
import type { ProviderShapeSpec } from '../providers/types';

const LEGACY_AWS_CONTAINER_MANIFEST_IDS = [
  'aws_account',
  'aws_region',
  'aws_vpc',
  'aws_subnet',
  'aws_subnet_public',
  'aws_subnet_private',
] as const;

const AWS_CATALOG_GROUP_CONTAINER_IDS = new Set<string>([
  'aws_catalog_group_aws-account',
  'aws_catalog_group_aws-cloud',
  'aws_catalog_group_region',
  'aws_catalog_group_virtual-private-cloud-vpc',
  'aws_catalog_group_public-subnet',
  'aws_catalog_group_private-subnet',
]);

export const NETWORK_CONTAINER_MANIFEST_IDS = new Set<string>([
  ...LEGACY_AWS_CONTAINER_MANIFEST_IDS,
  ...CONTAINER_PROVIDER_SHAPE_IDS,
  ...ALL_PROVIDER_SHAPE_SPECS
    .filter((shapeSpec) => isAwsCatalogGroupContainer(shapeSpec))
    .map((shapeSpec) => shapeSpec.id),
]);

export function isNetworkContainerManifestId(id: string | undefined | null): boolean {
  return typeof id === 'string' && NETWORK_CONTAINER_MANIFEST_IDS.has(id);
}

function isAwsCatalogGroupContainer(shapeSpec: ProviderShapeSpec): boolean {
  return shapeSpec.dialectId === 'aws' && AWS_CATALOG_GROUP_CONTAINER_IDS.has(shapeSpec.id);
}

export interface FrameBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface FrameLayoutInput {
  /** Container id (canvas id). */
  id: string;
  /** Display name. */
  name: string;
  /** Manifest id (for border color + metadata). */
  manifestId: string;
  /** Optional parent container id (creates Frame nesting). */
  parentId?: string;
  /**
   * Optional placeholder bounds hint, used when the container has no
   * leaves and no child containers — without this the frame would
   * collapse to (0,0) and stack on top of every other empty frame.
   * Callers (compose_service, apply_pattern) compute reserved
   * positions in their leaf-layout pass and pass them in here.
   */
  boundsHint?: FrameBounds;
}

export interface LeafLayoutInput {
  /** Canvas id of the leaf shape. */
  id: string;
  /** Position on canvas. */
  x: number;
  y: number;
  /** Dimensions. */
  width: number;
  height: number;
  /** Owning container's canvas id, if any. */
  parentId?: string;
}

export interface FrameDescriptor {
  id: string;
  name: string;
  bounds: FrameBounds;
  /** Direct LEAF member shape canvas ids — NOT transitive through child frames. */
  shapes: string[];
  /** Parent Frame id when nested. */
  parentFrame?: string;
  /** Manifest id (for border color + metadata). */
  manifestId: string;
}

export interface PlanContainerFramesOptions {
  padding?: number;
  titleHeight?: number;
}

const DEFAULT_PADDING = 24;
const DEFAULT_TITLE_HEIGHT = 32;

/**
 * Compute Frame descriptors for every container, bottom-up.
 *
 * Algorithm (mirrors hydrate's `buildContainerFrames`):
 *   1. Build child-of-container map from `leaves[].parentId` +
 *      `containers[].parentId`.
 *   2. Topological pass: ready = containers whose own container
 *      children are all already built. Compute bounds from their
 *      direct leaves + already-built child frames.
 *   3. Defensive cycle break: if no container is ready in a pass,
 *      bail with the remainder unresolved.
 *
 * Returns descriptors in build order — emit Frames in this order so
 * parents reference already-existing child Frames.
 */
export function planContainerFrames(
  containers: ReadonlyArray<FrameLayoutInput>,
  leaves: ReadonlyArray<LeafLayoutInput>,
  options: PlanContainerFramesOptions = {},
): FrameDescriptor[] {
  const padding = options.padding ?? DEFAULT_PADDING;
  const titleHeight = options.titleHeight ?? DEFAULT_TITLE_HEIGHT;

  // Index containers + leaves by id.
  const containerById = new Map(containers.map((c) => [c.id, c]));
  const leavesByParent = new Map<string, LeafLayoutInput[]>();
  for (const leaf of leaves) {
    if (!leaf.parentId) continue;
    if (!leavesByParent.has(leaf.parentId)) leavesByParent.set(leaf.parentId, []);
    leavesByParent.get(leaf.parentId)!.push(leaf);
  }
  const childContainersByParent = new Map<string, string[]>();
  for (const c of containers) {
    if (!c.parentId) continue;
    if (!childContainersByParent.has(c.parentId)) childContainersByParent.set(c.parentId, []);
    childContainersByParent.get(c.parentId)!.push(c.id);
  }

  const built = new Map<string, FrameBounds>();
  const remaining = new Set(containers.map((c) => c.id));
  const ordered: string[] = [];

  let safety = containers.length + 1;
  while (remaining.size > 0 && safety-- > 0) {
    const readyNow: string[] = [];
    for (const id of remaining) {
      const childContainers = childContainersByParent.get(id) ?? [];
      const allChildrenReady = childContainers.every((cid) => built.has(cid));
      if (allChildrenReady) readyNow.push(id);
    }
    if (readyNow.length === 0) break; // cycle / orphan — bail defensively
    for (const id of readyNow) {
      const bounds = computeBounds(id, {
        leavesByParent,
        childContainersByParent,
        built,
        padding,
        titleHeight,
        boundsHint: containerById.get(id)?.boundsHint,
      });
      if (bounds) {
        built.set(id, bounds);
        ordered.push(id);
      }
      remaining.delete(id);
    }
  }

  return ordered.map((id) => {
    const container = containerById.get(id)!;
    const directLeaves = leavesByParent.get(id) ?? [];
    return {
      id: container.id,
      name: container.name,
      bounds: built.get(id)!,
      shapes: directLeaves.map((l) => l.id),
      parentFrame: container.parentId,
      manifestId: container.manifestId,
    };
  });
}

function computeBounds(
  containerId: string,
  ctx: {
    leavesByParent: Map<string, LeafLayoutInput[]>;
    childContainersByParent: Map<string, string[]>;
    built: Map<string, FrameBounds>;
    padding: number;
    titleHeight: number;
    boundsHint?: FrameBounds;
  },
): FrameBounds | null {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  let included = 0;

  for (const leaf of ctx.leavesByParent.get(containerId) ?? []) {
    minX = Math.min(minX, leaf.x);
    minY = Math.min(minY, leaf.y);
    maxX = Math.max(maxX, leaf.x + leaf.width);
    maxY = Math.max(maxY, leaf.y + leaf.height);
    included += 1;
  }
  for (const childContainerId of ctx.childContainersByParent.get(containerId) ?? []) {
    const childBounds = ctx.built.get(childContainerId);
    if (!childBounds) continue;
    minX = Math.min(minX, childBounds.x);
    minY = Math.min(minY, childBounds.y);
    maxX = Math.max(maxX, childBounds.x + childBounds.width);
    maxY = Math.max(maxY, childBounds.y + childBounds.height);
    included += 1;
  }
  if (included === 0) {
    // Empty container — use caller-provided bounds hint when
    // available so the Frame lands in its reserved slot rather than
    // collapsing onto every other empty frame at the origin.
    if (ctx.boundsHint) return { ...ctx.boundsHint };
    return {
      x: 0,
      y: 0,
      width: 240 + ctx.padding * 2,
      height: 120 + ctx.padding * 2 + ctx.titleHeight,
    };
  }
  return {
    x: minX - ctx.padding,
    y: minY - ctx.padding - ctx.titleHeight,
    width: maxX - minX + ctx.padding * 2,
    height: maxY - minY + ctx.padding * 2 + ctx.titleHeight,
  };
}

/** Border color for a container Frame — mirrors hydrate's palette. */
export function borderColorForContainer(manifestId: string): string {
  switch (manifestId) {
    case 'aws_account': return '#0ea5e9';
    case 'aws_region': return '#a855f7';
    case 'aws_vpc': return '#22c55e';
    case 'aws_subnet':
    case 'aws_subnet_public':
    case 'aws_subnet_private':
      return '#94a3b8';
    default:
      return getProviderShapeSpec(manifestId)?.color ?? '#cbd5e1';
  }
}
